// React's side of the store, for containers. Every hook takes the plugin's
// `getApi` and binds to the one store `shared-store` registers: a plugin
// never creates or imports a store of its own.
import * as React from 'react';
import { STORE_API, type GameStore, type StoreState } from './store.ts';
import type { Aggregator } from './aggregator.ts';

type GetApi = <T>(name: string) => Promise<T>;

// The store once found, per `getApi`: a component mounted later (a screen
// shown again after going back) renders with the store from its first
// render, not one render later. Without it every remount drew the loading
// skeleton first, and <ScrollRestoration /> restored the scroll against the
// skeleton's height instead of the list's.
const found = new WeakMap<GetApi, GameStore>();

/** The store, once its provider has registered it; undefined until then. */
export function useGameStore(getApi: GetApi): GameStore | undefined {
  const [store, setStore] = React.useState<GameStore | undefined>(() => found.get(getApi));
  React.useEffect(() => {
    if (store) return;
    let live = true;
    void getApi<GameStore>(STORE_API).then((s) => {
      found.set(getApi, s);
      if (live) setStore(s);
    });
    return () => {
      live = false;
    };
  }, [getApi, store]);
  return store;
}

/** The store and its state, re-rendered on every change; both undefined until the provider registers. */
export function useStoreState(getApi: GetApi): { store?: GameStore; state?: StoreState } {
  const store = useGameStore(getApi);
  const subscribe = React.useCallback((notify: () => void) => (store ? store.subscribe(notify) : () => undefined), [store]);
  const read = () => store?.getState();
  const state = React.useSyncExternalStore(subscribe, read, read);
  return { store, state };
}

/**
 * Something computed from the store, recomputed only when the state changes
 * or `deps` do: a list, a count, a lookup. `compute` gets the store with its
 * state; undefined until the store is ready.
 */
export function useStoreQuery<T>(getApi: GetApi, compute: (store: GameStore, state: StoreState) => T, deps: React.DependencyList): T | undefined {
  const { store, state } = useStoreState(getApi);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => (store && state?.status === 'ready' ? compute(store, state) : undefined), [store, state, ...deps]);
}

/**
 * Registers a card's aggregator with the store, once per plugin: call from
 * the plugin's `setup`. Returns the unregister, for a plugin torn down.
 */
export function registerAggregator<R>(getApi: GetApi, aggregator: Aggregator<R>): () => void {
  let unregister: (() => void) | undefined;
  let gone = false;
  void getApi<GameStore>(STORE_API).then((store) => {
    if (!gone) unregister = store.registerAggregator(aggregator);
  });
  return () => {
    gone = true;
    unregister?.();
  };
}

/**
 * A card's result over the period, with the store's state (for the period
 * summary); the result is undefined until the aggregator has run.
 */
export function useAggregate<R>(getApi: GetApi, aggregator: Aggregator<R>): { result?: R; state?: StoreState } {
  const { store, state } = useStoreState(getApi);
  // Results live in the state, one object per run, so this is stable between changes.
  const result = state?.status === 'ready' ? store?.result(aggregator) : undefined;
  return { result, state };
}
